import { useMemo } from "react";

import type { Deployment } from "../../api/types";
import { graphicsOf, placementOf } from "../dossierWrites";
import { useDeployment } from "../units/useDeployment";
import { graphicPoints, graphicsCount, handPoints, handUnits } from "./orientDeployment";
import {
  angleGap,
  arcs,
  type Feasibility,
  type FeasibilityInput,
  feasibility,
  type OrientationModel,
  orientationModel,
  snapToTemplate,
  verdict,
} from "./orientation";
import { useOrientation } from "./useOrientation";

/** Ce que voient le panneau, le bandeau et la carte en mode « Orienter la manœuvre » (docs/14 §2.14) : modèle de
 *  calcul, points qui doivent rester dans le camp, sens possibles et verdict du sens visé. Calculé une fois par
 *  déploiement et par choix des graphiques (cache), partagé par tous les composants du mode. */
export interface OrientationView {
  model: OrientationModel;
  input: FeasibilityInput;
  feasibility: Feasibility;
  /** Secteurs possibles [premier, dernier] (caps entiers). */
  arcs: [number, number][];
  /** Graphiques tactiques du dossier, unités posées à la main, fuseau retouché. */
  graphicsTotal: number;
  handUnits: number;
  custom: boolean;
  /** Sens visé (degrés) et sa cause d'impossibilité (null : possible). */
  target: number;
  reason: Feasibility["reasons"][number];
  /** Le sens visé est le sens appliqué : rien à enregistrer. */
  unchanged: boolean;
}

interface Inputs {
  model: OrientationModel;
  input: FeasibilityInput;
  feasibility: Feasibility;
  arcs: [number, number][];
  graphicsTotal: number;
  handUnits: number;
  custom: boolean;
}

const CACHE = new WeakMap<Deployment, Map<boolean, Inputs | null>>();

/** Entrées du mode pour un déploiement (null : terrain libre, CENTAC, fuseau antérieur, sans état). */
export function orientationInputs(deployment: Deployment, graphics: boolean): Inputs | null {
  let byChoice = CACHE.get(deployment);
  if (!byChoice) CACHE.set(deployment, (byChoice = new Map()));
  if (byChoice.has(graphics)) return byChoice.get(graphics)!;
  const block = deployment.corridor?.orientation;
  const model = block?.editable ? orientationModel(block) : null;
  let out: Inputs | null = null;
  if (model) {
    const state = placementOf(deployment);
    const stored = deployment.placement?.corridor;
    const tactical = graphicsOf(deployment);
    const input: FeasibilityInput = {
      handPoints: handPoints(state),
      graphicPoints: graphics ? graphicPoints(tactical) : [],
      corridor: state.corridor ? [...state.corridor.left, ...state.corridor.right] : [],
      seed: state.corridor && stored?.seed ? [...stored.seed.left, ...stored.seed.right] : [],
    };
    const result = feasibility(model, input);
    out = {
      model,
      input,
      feasibility: result,
      arcs: arcs(result.possible),
      graphicsTotal: graphicsCount(tactical),
      handUnits: handUnits(state),
      custom: Boolean(state.corridor),
    };
  }
  byChoice.set(graphics, out);
  return out;
}

/** Vue du mode pour un déploiement et un sens visé (fonction pure). */
export function orientationViewOf(deployment: Deployment | undefined, target: number | null, graphics: boolean): OrientationView | null {
  if (!deployment || target === null) return null;
  const inputs = orientationInputs(deployment, graphics);
  if (!inputs) return null;
  const snapped = snapToTemplate(target, inputs.model.template);
  return {
    ...inputs,
    target: snapped,
    reason: verdict(inputs.model, inputs.input, snapped),
    unchanged: angleGap(snapped, inputs.model.current.bearing) < 0.05,
  };
}

/** Vue du mode ouvert (null mode fermé ou sans état). */
export function useOrientationView(): OrientationView | null {
  const { data } = useDeployment();
  const open = useOrientation((s) => s.open);
  const target = useOrientation((s) => s.target);
  const graphics = useOrientation((s) => s.graphics);
  return useMemo(() => (open ? orientationViewOf(data, target, graphics) : null), [data, open, target, graphics]);
}
